import type { AcademicLevelDto } from '@_/api-contracts/common.ts';
import { Fragment, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { groupAcademicLevels } from '@_/utils';

interface GroupedAcademicLevelsTextProps {
  academicLevels: AcademicLevelDto[];
  renderGroup: (label: string, index: number) => React.ReactNode;
  shortName?: boolean;
}

export function GroupedAcademicLevelsText({
  academicLevels,
  renderGroup,
  shortName = false
}: GroupedAcademicLevelsTextProps) {
  const { locale } = useIntl();

  const groups = useMemo(() => groupAcademicLevels(academicLevels), [academicLevels]);

  const nameKey = shortName ? 'shortName' : 'name';

  const rangeChar = locale.startsWith('zh') ? '至' : '\u2013'; // en dash

  const labels = groups.map(group => {
    if (!group[0].curriculum) {
      throw new Error('Group does not have a curriculum');
    }

    const curriculum = group[0].curriculum[nameKey];
    const levels =
      group.length === 1 ? [group[0][nameKey]] : [group[0][nameKey], group[1][nameKey]];

    const label =
      group.length === 1
        ? [curriculum, levels[0]].join(' ')
        : [curriculum, levels[0], rangeChar, levels[1]].join(' ');

    return {
      key: group[0].id + (group.length > 1 ? `-${group[1].id}` : ''),
      label
    };
  });

  return (
    <>
      {labels.map(({ key, label }, index) => (
        <Fragment key={key}>{renderGroup(label, index)}</Fragment>
      ))}
    </>
  );
}
